<script setup lang="ts">
import ProjectLinkCard from '@/components/cards/ProjectLinkCard.vue';
import ProjectCardSkeleton from '@/components/cards/ProjectCardSkeleton.vue';
import type { Project } from '@/typings/projects/Project.ts';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls.ts';
import { useI18n } from 'vue-i18n';
import type { LocaleSchema } from '@/locales/index.ts';
import { ListingTypeEnum } from '@/enums/ListingType';

const { t, locale } = useI18n<{message: LocaleSchema}>({
  useScope: 'global',
});

const selectedProject = defineModel<Project>('selected');
const hoveredProject = defineModel<Project>('hovered');
const { getProjectWizardUrl } = useRentalLocalizedUrls();

const props = defineProps<{
  projects: Project[];
  loading?: boolean;
  listingType: ListingTypeEnum;
  isSelectable?: boolean;
}>();

const emit = defineEmits<{
  'project-selected': [project: Project];
}>();

function onProjectClick(project: Project) {
  if (props.isSelectable) {
    selectedProject.value = project;
    emit('project-selected', project);
  }
}

function onProjectHover(project: Project | undefined) {
  if (props.isSelectable) {
    hoveredProject.value = project;
  }
}
</script>

<template>
  <div class="projectList">
    <div v-if="loading" class="projectList-loadingState">
      <ProjectCardSkeleton />
    </div>
    <div v-else-if="projects.length > 0" class="projectList-projectGrid preferredColumnCountGrid">
      <ProjectLinkCard
        v-for="project in projects"
        :key="project.id"
        class="projectList-projectCard"
        :class="{
          'projectList-projectCard--selected': isSelectable && selectedProject?.id === project.id,
          'projectList-projectCard--hovered': isSelectable && hoveredProject?.id === project.id,
          'projectList-projectCard--selectable': isSelectable
        }"
        :project="project"
        :href="!isSelectable ? getProjectWizardUrl(project) : ''"
        :h4Title="false"
        :listingType="props.listingType"
        @click="isSelectable ? onProjectClick(project) : undefined"
        @mouseenter="isSelectable ? onProjectHover(project) : undefined"
        @mouseleave="isSelectable ? onProjectHover(undefined) : undefined">
        <template #extra-info>
          <slot name="extra-info" :project="project">
          </slot>
        </template>
        <template #footer>
          <slot name="footer" :project="project">
          </slot>
        </template>
      </ProjectLinkCard>
    </div>
    <div v-else class="projectList-emptyState">
      <h3>{{ t('browsingApp.projects.noProjectsAvailable') }}</h3>
    </div>
  </div>
</template>

<style lang="scss">
.projectList {
  &-projectGrid {
    --column-count: 2;
    --column-gap: var(--spc24-16);
    max-width: 100%;
  }

  &-emptyState {
    text-align: left;
    padding: 2rem 0;
    color: var(--gray-500);

    h3 {
      margin: 0;
      font-weight: 500;
      font-style: italic;
    }
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Selectable project card styles */
.projectList-projectCard {
  &--selectable {
    cursor: pointer;
    
    a {
      pointer-events: none; // Désactiver le lien normal quand sélectionnable
    }
    
    .cardTopImage_projectLinkCard {
      transition: all 0.2s ease;
    }
  }
  
  &--hovered:not(&--selected) .cardTopImage_projectLinkCard {
    outline-color: var(--primary-300);
    outline-offset: -2px;
    transform: translateY(-2px);
  }
  
  &--selected .cardTopImage_projectLinkCard {
    outline: 3px solid var(--primary-500);
    outline-offset: -3px;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}
</style>
